<script>
import ADD_MEMO_LIST_TO_CART from 'gql/addMemoListToCart.gql';
import CREATE_MEMO_LIST from 'gql/createMemoList.gql';
import GET_MEMO_LIST from 'gql/getMemoList.gql';
import REPLACE_CART_WITH_MEMO_LIST from 'gql/replaceCartWithMemoList.gql';
import UPDATE_MEMO_LIST from 'gql/updateMemoList.gql';
import eventHubMixin from 'mixins/eventHubMixin';
import shoppingcartMixin from 'mixins/shoppingcartMixin';
import { getIsIds } from 'utils/idsTools';

export default {
  mixins: [shoppingcartMixin, eventHubMixin],
  emits: ['new-list-saved'],
  data() {
    return {
      savedCart: null,
      useCartCode: true,
      isUpdating: false,
      isUpdatingCart: false,
      isUpdatingAddtoCart: false,
      nameMaxLength: 40,
      descriptionMaxLength: 255,
    };
  },
  computed: {
    memoListOrSavedCart() {
      return this.memoList.newListId ? this.savedCart || {} : this.memoList;
    },
    productCount() {
      return this.savedCart?.totalItems || 0;
    },
    cartCode() {
      return this.savedCart?.code || null;
    },
    namePlaceholder() {
      return this.$dict.get('memo-List.New-Name-Template', [this.isoDate]);
    },
    cartName() {
      return this.nameInput || this.namePlaceholder;
    },
    isoDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = `0${date.getMonth() + 1}`.substr(-2);
      const day = `0${date.getDate()}`.substr(-2);
      return `${year}-${month}-${day}`;
    },
  },
  methods: {
    triggerErrorMessage() {
      if (this.$refs.errorMessage) {
        this.$refs.errorMessage.trigger();
      }
    },
    async getCartCode(resolve, reject) {
      if (!this.cartCode) {
        const saveSuccess = await this.saveMetaData(this.cartName, this.descriptionInput || '');
        if (!(saveSuccess && this.savedCart?.code)) {
          reject();
          return;
        }
      }
      resolve(this.cartCode);
    },
    async saveMetaData(cartName, cartDescription) {
      try {
        this.isUpdating = true;

        const variables = {
          baseSiteId: this.$fischer.baseSiteId(),
          accessToken: this.$fischer.user.accessToken(),
          cartName,
          cartDescription,
          userId: this.$fischer.user.id(),
        };
        let mutation;
        const oldSavedCart = this.savedCart || this.memoList;
        if (oldSavedCart.code) {
          mutation = UPDATE_MEMO_LIST;
          variables.code = oldSavedCart.code;
        } else {
          mutation = CREATE_MEMO_LIST;
        }

        const {
          data: { createMemoList, updateMemoList },
        } = await this.$apollo.mutate({
          mutation,
          variables,
        });
        const savedCart = createMemoList || updateMemoList;
        if (this.memoList.newListId && savedCart.code) {
          this.$emit('new-list-saved', this.memoList.newListId, savedCart.code);
        }
        this.savedCart = savedCart;

        if (!variables.code) {
          this.sendEvent('memo_list_create');
        }
      } catch (error) {
        console.error('Could not save memo list', error);
        this.triggerErrorMessage();
        return false;
      } finally {
        this.isUpdating = false;
      }
      return true;
    },
    async getCart() {
      try {
        if (!this.cartCode) {
          return;
        }
        this.isUpdatingCart = true;

        const {
          data: { getMemoList },
        } = await this.$apollo.query({
          query: GET_MEMO_LIST,
          fetchPolicy: 'no-cache',
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            code: this.cartCode,
            userId: this.$fischer.user.id(),
          },
        });
        this.savedCart = getMemoList;
      } catch (error) {
        console.error(error);
        this.triggerErrorMessage();
      } finally {
        this.isUpdatingCart = false;
      }
    },
    async addMemoListToCart() {
      try {
        this.isUpdatingAddtoCart = true;
        const {
          data: {
            addMemoListToCart: { guid },
          },
        } = await this.$apollo.mutate({
          mutation: ADD_MEMO_LIST_TO_CART,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            memoListCode: this.cartCode,
            isIds: getIsIds(),
            userId: this.$fischer.user.id(),
            lang: this.$fischer.language_639_1(),
          },
        });
        if (!guid) {
          throw new Error('Received no cart guid!');
        }
        if (this.$fischer.cartId !== guid) {
          await this.$fischer.setCartId(guid);
        }
        this.notifyCartChange();
        this.sendEvent('memo_list_cart', {
          memoListId: this.cartCode,
        });
      } catch (error) {
        console.error(error);
        this.triggerBuyMemoList();
      } finally {
        this.isUpdatingAddtoCart = false;
      }
    },
    async replaceCartWithMemoList() {
      try {
        this.isUpdatingAddtoCart = true;
        const {
          data: {
            replaceCartWithMemoList: { guid },
          },
        } = await this.$apollo.mutate({
          mutation: REPLACE_CART_WITH_MEMO_LIST,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            memoListCode: this.cartCode,
            isIds: getIsIds(),
            userId: this.$fischer.user.id(),
            lang: this.$fischer.language_639_1(),
          },
        });
        if (!guid) {
          throw new Error('Received no cart guid!');
        }
        if (this.$fischer.cartId !== guid) {
          await this.$fischer.setCartId(guid);
        }
        this.notifyCartChange();
        this.sendEvent('memo_list_cart', {
          memoListId: this.cartCode,
        });
      } catch (error) {
        console.error(error);
        this.isUpdatingAddtoCart = false;
        this.triggerBuyMemoList();
      } finally {
        this.isUpdatingAddtoCart = false;
      }
    },
    async triggerBuyMemoList() {
      if (this.productCount === 0) {
        return;
      }
      try {
        if (!this.$refs.addToCartDialog) {
          console.error('Could not find addToCartDialog');
          this.triggerErrorMessage();
          return;
        }

        const count = await this.getCartPositionsCount();
        if (count > 0) {
          this.$refs.addToCartDialog.show();
        } else {
          await this.addMemoListToCart();
        }
      } catch (error) {
        console.error(error);
        this.triggerErrorMessage();
      } finally {
        this.isUpdatingAddtoCart = false;
      }
    },
  },
};
</script>
